import * as Yup from "yup";
import { AutoLBType, ClaimStatus } from "../../../types/claim";

const estimateField = Yup.number()
  .nullable()
  .min(0, "Estimate must be a positive amount")
  .transform((value, originalValue) =>
    originalValue === "" || originalValue === null ? null : value
  );

export const claimValidationSchema = Yup.object().shape({
  dateOfLoss: Yup.string().required("Date of loss is required"),
  status: Yup.number().required("Status is required"),
  city: Yup.string().nullable().max(100, "City cannot exceed 100 characters"),
  state: Yup.string().nullable().max(50, "State cannot exceed 50 characters"),
  totalEstimate: estimateField,
  truckEstimate: estimateField,
  trailerEstimate: estimateField,
  description: Yup.string().nullable().max(4000, "Description cannot exceed 4000 characters"),
  note: Yup.string().nullable().max(4000, "Note cannot exceed 4000 characters"),
  adjusterName: Yup.string().nullable().max(200, "Adjuster name cannot exceed 200 characters"),
  adjusterPhone: Yup.string().nullable().max(50, "Adjuster phone cannot exceed 50 characters"),
  adjusterEmail: Yup.string()
    .nullable()
    .email("Adjuster email must be a valid email address")
    .max(200, "Adjuster email cannot exceed 200 characters"),
  claimNumber: Yup.string().nullable().max(100, "Claim number cannot exceed 100 characters"),
  autoLB: Yup.number()
    .nullable()
    .oneOf([AutoLBType.AlPd, AutoLBType.Cargo], "Invalid Auto LB type"),
  policeReportDescription: Yup.string()
    .nullable()
    .max(2000, "Police report description cannot exceed 2000 characters"),
});

export interface ClaimFormValues {
  dateOfLoss: string;
  city: string;
  state: string;
  claimCompanyId: number | null;
  claimCompanyName: string | null;
  assignedToUserId: number | null;
  driverEmployeeId: number | null;
  vehicleId: number | null;
  trailerId: number | null;
  description: string;
  claimNumber: string;
  note: string;
  adjusterName: string;
  adjusterPhone: string;
  adjusterEmail: string;
  autoLB: AutoLBType | null;
  policeReportDescription: string;
  /** Legacy total from DB when truck/trailer were never entered */
  legacyTotalEstimate: number | null;
  truckEstimate: number | string;
  trailerEstimate: number | string;
  status: ClaimStatus;
  driverCharged: number | null;
}

export const parseEstimate = (value: number | string | null | undefined): number | null => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Sum of truck + trailer when either is set; otherwise null (keep legacy total). */
export const computeTotalEstimate = (
  truckEstimate: number | string | null | undefined,
  trailerEstimate: number | string | null | undefined,
  legacyTotalEstimate: number | null = null
): number | null => {
  const truck = parseEstimate(truckEstimate);
  const trailer = parseEstimate(trailerEstimate);
  if (truck == null && trailer == null) {
    return legacyTotalEstimate;
  }
  return (truck ?? 0) + (trailer ?? 0);
};

export const getInitialClaimFormValues = (): ClaimFormValues => ({
  dateOfLoss: new Date().toISOString().split("T")[0],
  city: "",
  state: "",
  claimCompanyId: null,
  claimCompanyName: null,
  assignedToUserId: null,
  driverEmployeeId: null,
  vehicleId: null,
  trailerId: null,
  description: "",
  claimNumber: "",
  note: "",
  adjusterName: "",
  adjusterPhone: "",
  adjusterEmail: "",
  autoLB: null,
  policeReportDescription: "",
  legacyTotalEstimate: null,
  truckEstimate: "",
  trailerEstimate: "",
  status: ClaimStatus.NotStartedYet,
  driverCharged: null,
});
